export default `<p><strong>Version du document :</strong> V3.28 &nbsp;|&nbsp; <strong>Date de publication :</strong> 2022 &nbsp;|&nbsp; <strong>Type :</strong> Livre blanc produit et résumé d'évaluation de sécurité</p>

<h2>1. Résumé exécutif</h2>
<p>Ce module Lending Pool est un service de prêt et de liquidité on-chain construit sur des protocoles DeFi de marché monétaire. Grâce à des smart contracts open source et des modèles de taux programmatiques, le système connecte les actifs numériques des utilisateurs aux marchés de prêt on-chain établis, permettant <strong>des rendements sur dépôts, des emprunts garantis et le partage de liquidité</strong>.</p>
<p>Le module ne repose pas sur une intervention manuelle pour l'allocation quotidienne des fonds. Les taux, capacités et taux d'utilisation sont déterminés en temps réel par les marchés on-chain et affichés de manière transparente via l'agrégation de données multi-sources.</p>

<h2>2. Contexte sectoriel et positionnement</h2>
<p>Le crédit traditionnel dépend d'institutions centralisées. Les protocoles DeFi utilisent des smart contracts pour faire correspondre automatiquement l'offre et la demande, avec sur-collatéralisation et mécanismes de liquidation.</p>
<p>Positionnement de ce module :</p>
<ul>
  <li><strong>Passerelle vers les marchés monétaires on-chain :</strong> agrège les données de pools sur plusieurs chaînes et protocoles ;</li>
  <li><strong>Distribution programmatique des rendements :</strong> règlement automatique selon un calendrier fixe basé sur les soldes autorisés et l'APY du marché ;</li>
  <li><strong>Transparent et vérifiable :</strong> TVL, APY, utilisation et LTV traçables vers des données on-chain publiques ;</li>
  <li><strong>Non custodial :</strong> l'autorisation et les interactions on-chain sont initiées depuis le portefeuille de l'utilisateur.</li>
</ul>

<h2>3. Architecture système</h2>
<h3>3.1 Vue d'ensemble</h3>
<ul>
  <li><strong>Interface utilisateur :</strong> connexion wallet, autorisation, navigation des pools, consultation des gains ;</li>
  <li><strong>Couche métier :</strong> authentification, gestion des autorisations, génération d'ordres de rendement, contenu multilingue ;</li>
  <li><strong>Couche d'agrégation :</strong> synchronisation planifiée des métriques depuis des sources DeFi publiques ;</li>
  <li><strong>Couche protocole on-chain :</strong> smart contracts de prêt audités exécutant la logique de dépôt et d'emprunt.</li>
</ul>

<h3>3.2 Métriques clés</h3>
<table>
  <thead>
    <tr><th>Métrique</th><th>Description</th></tr>
  </thead>
  <tbody>
    <tr><td>TVL</td><td>Actifs totaux du pool en USD</td></tr>
    <tr><td>Supply APY</td><td>Rendement annuel des dépôts (taux de base + récompenses)</td></tr>
    <tr><td>Borrow APY</td><td>Coût annuel payé par les emprunteurs</td></tr>
    <tr><td>Taux d'utilisation</td><td>Ratio des fonds empruntés sur fonds disponibles</td></tr>
    <tr><td>Max LTV</td><td>Ratio prêt/valeur maximal avant liquidation</td></tr>
    <tr><td>Supply / Borrow Cap</td><td>Limites par actif pour contrôler le risque de concentration</td></tr>
    <tr><td>Liquidité disponible</td><td>Liquidité immédiatement empruntable</td></tr>
  </tbody>
</table>

<h2>4. Parcours utilisateur et mécanisme de rendement</h2>
<ol>
  <li><strong>Connecter le wallet</strong> via Web3 ;</li>
  <li><strong>Autoriser les actifs</strong> (USDT, USDC, etc.) par Approve on-chain ;</li>
  <li><strong>Rejoindre le lending pool</strong> pour entrer dans la file de calcul des rendements ;</li>
  <li><strong>Règlement des gains</strong> quotidien selon le solde autorisé et les taux du marché ;</li>
  <li><strong>Consulter les actifs</strong> dans « Mon portefeuille ».</li>
</ol>
<p><strong>L'APY historique ne garantit pas les rendements futurs.</strong></p>

<h3>4.3 Frais</h3>
<h4>Comment sont facturés les frais DeFi Earn ?</h4>
<p>Les transactions on-chain nécessitent des frais de gas pour interagir avec les smart contracts, y compris la souscription et la réclamation des gains. Pour certains actifs, le lending pool DeFi et les gestionnaires de vault peuvent prélever des frais de performance en pourcentage de vos profits réels ; votre capital n'est pas affecté.</p>

<h2>5. Conception de la sécurité</h2>
<ul>
  <li>Protocoles sous-jacents audités en open source ;</li>
  <li>Architecture non custodiale ; aucune clé privée stockée ;</li>
  <li>Permissions Token Approve minimales ; révocables à tout moment ;</li>
  <li>Authentification par token, HTTPS, RBAC admin et journaux d'audit.</li>
</ul>

<h2>6. Résumé de l'évaluation de sécurité</h2>
<table>
  <thead>
    <tr><th>Élément</th><th>Résultat</th><th>Notes</th></tr>
  </thead>
  <tbody>
    <tr><td>Dépendances smart contract</td><td><strong>Validé</strong></td><td>Protocoles DeFi audités publiquement</td></tr>
    <tr><td>Non custodial et gestion des clés</td><td><strong>Validé</strong></td><td>Aucun stockage de clé privée</td></tr>
    <tr><td>API et authentification</td><td><strong>Validé</strong></td><td>Auth token et HTTPS en place</td></tr>
    <tr><td>Synchronisation des données</td><td><strong>Validé</strong></td><td>Sync planifiée avec snapshots</td></tr>
    <tr><td>Logique de règlement des gains</td><td><strong>Validé</strong></td><td>Règles explicites ; ordres auditable</td></tr>
  </tbody>
</table>

<h2>7. Divulgation des risques</h2>
<ul>
  <li><strong>Risque smart contract :</strong> vulnérabilités non découvertes possibles ;</li>
  <li><strong>Risque de marché :</strong> l'APY peut fluctuer fortement ;</li>
  <li><strong>Risque de liquidation :</strong> baisse du collatéral pouvant déclencher une liquidation ;</li>
  <li><strong>Risque de dépeg des stablecoins :</strong> USDT/USDC peut s'écarter de 1,00 $ ;</li>
  <li><strong>Risque réglementaire :</strong> la régulation DeFi évolue mondialement.</li>
</ul>
<p><strong>Ce module ne fournit pas de conseil en investissement et ne garantit aucun rendement fixe.</strong></p>

<h2>8. Glossaire</h2>
<ul>
  <li><strong>APY :</strong> rendement annuel en pourcentage ;</li>
  <li><strong>TVL :</strong> valeur totale verrouillée ;</li>
  <li><strong>LTV :</strong> ratio prêt/valeur ;</li>
  <li><strong>Approve :</strong> autorisation de token ERC-20 ;</li>
  <li><strong>Liquidation :</strong> vente automatique du collatéral en cas de sous-collatéralisation.</li>
</ul>

<p>— À titre informatif uniquement. Ne constitue pas un conseil juridique, financier ou en investissement. Vérifiez les données on-chain via les explorateurs de blocs et les interfaces officielles des protocoles.</p>`
